import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
const BoundsRegistryContext = createContext(null);
export function useReportBounds(points) {
    const id = useId();
    const ctx = useContext(BoundsRegistryContext);
    const key = points && points.length ? JSON.stringify(points) : null;
    useEffect(() => {
        if (!ctx) {
            return;
        }
        ctx.report(id, key ? points : null);
        return () => ctx.report(id, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, id, key]);
}
const AutoFitBounds = ({ regions, enabled, zoom }) => {
    const map = useMap();
    const hasFitRef = useRef(false);
    // Re-arm the one-shot fit whenever boundMapToMarkers is (re)disabled, so a
    // consumer that toggles it off/on (e.g. after a tenant change) gets a fresh
    // initial fit, without us fighting the user's pan/zoom in between.
    useEffect(() => {
        if (!enabled) {
            hasFitRef.current = false;
        }
    }, [enabled]);
    useEffect(() => {
        if (!map || !enabled || hasFitRef.current) {
            return;
        }
        const points = Object.values(regions).flat();
        if (points.length === 0) {
            return;
        }
        hasFitRef.current = true;
        if (points.length === 1) {
            map.setCenter(points[0]);
            map.setZoom(zoom ?? 14);
        }
        else {
            const bounds = new google.maps.LatLngBounds();
            points.forEach(p => bounds.extend(p));
            map.fitBounds(bounds);
        }
    }, [map, enabled, zoom, regions]);
    return null;
};
export const BoundsRegistryProvider = ({ enabled, zoom, children }) => {
    const [regions, setRegions] = useState({});
    // Stable across renders: without this, every marker's registration effect
    // (which depends on the context value) would re-fire on any unrelated
    // re-render of this provider's subtree, causing AutoFitBounds to keep
    // re-fitting and fight the user's manual pan/zoom.
    const report = useCallback((id, points) => {
        setRegions(prev => {
            if (!points) {
                if (!(id in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[id];
                return next;
            }
            return { ...prev, [id]: points };
        });
    }, []);
    const contextValue = useMemo(() => ({ report }), [report]);
    return (_jsxs(BoundsRegistryContext.Provider, { value: contextValue, children: [children, _jsx(AutoFitBounds, { regions: regions, enabled: enabled, zoom: zoom })] }));
};
